import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type AnalyticsTypeCount = {
  type: string;
  count: number;
  /** Distinct users. Absent means unknown, never zero. */
  people?: number;
};

export type AnalyticsWindow = {
  window: string;
  total: number;
  byType: AnalyticsTypeCount[];
};

export const BETA_USAGE_FORMAT = 2;

export type BetaFeatureDef = {
  id: string;
  name: string;
  keyAction1: string;
  keyAction2: string;
  metrics: string[];
};

export type BetaDay = {
  day: string;
  counts: Record<string, number>;
};

export type BetaUser = {
  sub: string;
  /** Every sign-in the person's events arrived under, newest first. */
  subs: string[];
  userId?: string;
  districtId?: string;
  days: BetaDay[];
};

export type BetaUsage = {
  format: typeof BETA_USAGE_FORMAT;
  features: BetaFeatureDef[];
  users: BetaUser[];
  unattributed: number;
};

export type BetaAccessEntry = {
  sub: string;
  /** The stable identity behind the sub, when the grant recorded one. */
  userId?: string;
  feature: string;
  grantedAt: string;
};

export type BetaAccess = {
  entries: BetaAccessEntry[];
};

export type DistrictStat = {
  districtId: string;
  name?: string;
  count: number;
  p90Ms: number;
  maxMs: number;
  live: number;
  liveLoads: number;
};

export type RouteStats = {
  route: string;
  count: number;
  failures: number;
  unconfigured: number;
  p50Ms: number;
  p90Ms: number;
  maxMs: number;
  live: number;
  liveLoads: number;
  slowest: DistrictStat[];
};

export type ProbeStats = {
  routes: RouteStats[];
  lastRunAt?: string;
  districts: number;
};

/** The oldest data a cache served over the window, and how many loads read it. */
export type CacheStats = {
  cache: string;
  reads: number;
  oldestAgeMs: number;
  oldestAsOf?: string;
  oldestReadAt?: string;
  oldestDistrict?: string;
};

export type PerformanceWindow = {
  window: string;
  users: RouteStats[];
  probe: ProbeStats;
  /** Undefined for a snapshot written before cache ages were kept. */
  caches?: CacheStats[];
};

export type AnalyticsSnapshot = {
  computed: boolean;
  computedAt?: string;
  scanned?: number;
  windows: AnalyticsWindow[];
  betaUsage?: BetaUsage;
  /** Null means the access list could not be read, which is not an empty list. */
  betaAccess: BetaAccess | null;
  /** Undefined for a snapshot written before load times were kept. */
  performance?: PerformanceWindow[];
};

type RawRouteStats = Partial<Omit<RouteStats, 'slowest'>> & { slowest?: (Partial<DistrictStat> | null)[] | null };
type RawPerformanceWindow = Partial<Omit<PerformanceWindow, 'users' | 'probe' | 'caches'>> & {
  users?: (RawRouteStats | null)[] | null;
  probe?: (Partial<Omit<ProbeStats, 'routes'>> & { routes?: (RawRouteStats | null)[] | null }) | null;
  caches?: (Partial<CacheStats> | null)[] | null;
};

type RawBetaUsage = {
  format?: unknown;
  features?: (Partial<BetaFeatureDef> | null)[] | null;
  users?: (Partial<Omit<BetaUser, 'days'>> & { days?: (Partial<BetaDay> | null)[] | null } | null)[] | null;
  unattributed?: number;
};

type RawBetaAccess = { entries?: (Partial<BetaAccessEntry> | null)[] | null } | null;

type RawSnapshot = Partial<Omit<AnalyticsSnapshot, 'betaUsage' | 'betaAccess' | 'performance'>> & {
  windows?: (Partial<AnalyticsWindow> | null)[] | null;
  betaUsage?: RawBetaUsage | null;
  betaAccess?: RawBetaAccess;
  performance?: (RawPerformanceWindow | null)[] | null;
};

const ms = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0);

function routeStatsOf(raw: (RawRouteStats | null)[] | null | undefined): RouteStats[] {
  return (raw ?? []).flatMap((r) =>
    r?.route
      ? [
          {
            route: r.route,
            count: ms(r.count),
            failures: ms(r.failures),
            unconfigured: ms(r.unconfigured),
            p50Ms: ms(r.p50Ms),
            p90Ms: ms(r.p90Ms),
            maxMs: ms(r.maxMs),
            live: ms(r.live),
            liveLoads: ms(r.liveLoads),
            slowest: (r.slowest ?? []).flatMap((d) =>
              d?.districtId
                ? [{ districtId: d.districtId, ...(d.name ? { name: d.name } : {}), count: ms(d.count), p90Ms: ms(d.p90Ms), maxMs: ms(d.maxMs), live: ms(d.live), liveLoads: ms(d.liveLoads) }]
                : [],
            ),
          },
        ]
      : [],
  );
}

function cacheStatsOf(raw: (Partial<CacheStats> | null)[]): CacheStats[] {
  return raw.flatMap((c) =>
    c?.cache
      ? [
          {
            cache: c.cache,
            reads: ms(c.reads),
            oldestAgeMs: ms(c.oldestAgeMs),
            ...(c.oldestAsOf ? { oldestAsOf: c.oldestAsOf } : {}),
            ...(c.oldestReadAt ? { oldestReadAt: c.oldestReadAt } : {}),
            ...(c.oldestDistrict ? { oldestDistrict: c.oldestDistrict } : {}),
          },
        ]
      : [],
  );
}

function performanceOf(raw: (RawPerformanceWindow | null)[] | null | undefined): PerformanceWindow[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.flatMap((w) =>
    w?.window
      ? [
          {
            window: w.window,
            users: routeStatsOf(w.users),
            probe: { routes: routeStatsOf(w.probe?.routes), ...(w.probe?.lastRunAt ? { lastRunAt: w.probe.lastRunAt } : {}), districts: ms(w.probe?.districts) },
            ...(Array.isArray(w.caches) ? { caches: cacheStatsOf(w.caches) } : {}),
          },
        ]
      : [],
  );
}

const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;

function countsOf(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== 'object') return {};
  return Object.fromEntries(Object.entries(raw as Record<string, unknown>).flatMap(([metric, value]) => (isCount(value) ? [[metric, value]] : [])));
}

function betaUsageOf(raw: RawBetaUsage | null | undefined): BetaUsage | undefined {
  if (!raw || raw.format !== BETA_USAGE_FORMAT) return undefined;
  return {
    format: BETA_USAGE_FORMAT,
    features: (raw.features ?? []).flatMap((f) =>
      f?.id ? [{ id: f.id, name: f.name ?? f.id, keyAction1: f.keyAction1 ?? '', keyAction2: f.keyAction2 ?? '', metrics: f.metrics ?? [] }] : [],
    ),
    users: (raw.users ?? []).flatMap((u) =>
      u?.sub
        ? [
            {
              sub: u.sub,
              subs: [...new Set([u.sub, ...(u.subs ?? []).filter((alias): alias is string => typeof alias === 'string' && alias !== '')])],
              userId: u.userId,
              districtId: u.districtId,
              days: (u.days ?? []).flatMap((d) => (d?.day ? [{ day: d.day, counts: countsOf(d.counts) }] : [])),
            },
          ]
        : [],
    ),
    unattributed: raw.unattributed ?? 0,
  };
}

function betaAccessOf(raw: RawBetaAccess | undefined): BetaAccess | null {
  if (!raw || !Array.isArray(raw.entries)) return null;
  return {
    entries: raw.entries.flatMap((e) =>
      e?.sub && e.feature ? [{ sub: e.sub, userId: typeof e.userId === 'string' && e.userId !== '' ? e.userId : undefined, feature: e.feature, grantedAt: e.grantedAt ?? '' }] : [],
    ),
  };
}

export async function fetchAnalytics(): Promise<AnalyticsSnapshot> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/admin/analytics`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`admin-analytics fetch failed: ${response.status}`);
  }
  const data = ((await response.json()) as RawSnapshot | null) ?? {};
  return {
    computed: data.computed === true,
    computedAt: data.computedAt,
    scanned: data.scanned,
    windows: (data.windows ?? []).flatMap((window) =>
      window ? [{ window: window.window ?? '', total: window.total ?? 0, byType: window.byType ?? [] }] : [],
    ),
    betaUsage: betaUsageOf(data.betaUsage),
    betaAccess: betaAccessOf(data.betaAccess),
    performance: performanceOf(data.performance),
  };
}
